import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useToast } from '../../contexts/ToastContext'

import {
  Drawer,
  DrawerContent,
  DrawerNavigation,
} from '@progress/kendo-react-layout'
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs'
import { FaTimesCircle } from 'react-icons/fa'

import JobsKendoGrid from '../../components/grids/JobsKendoGrid'
import AddJobForm from '../../components/FormComps/Jobs/AddJobForm'
import EditJobForm from '../../components/FormComps/Jobs/EditJobForm'

import {
  submitJob,
  updateJob,
  exportJobs,
  exportJobsheet,
  getJobs, 
  getJobsWithPagination,
  completeJob, 
  deleteJob,
  submitJobSageTest,
  updateJobSageTest,
  getJobCostingData,
  JobCostingItem
} from '../../data/jobData'

import { DateFormatterReverse } from '../../data/helper'

import {
  jobsGrid,
  getSiteTypes,
  dropDown,
  clientsGrid,
} from '../../data/interfaces'

import { submitSite, getSites } from '../../data/siteData'
import { submitClient, getClients } from '../../data/clientData'
import {
  getWorktypeDropdown,
  postWorkType,
  submitWorktype,
} from '../../data/worktypeData'
// Note: These imports need to be checked for correct function names
// import { getDepartments } from '../../data/departmentData'
// import { getEmployees } from '../../data/employeeData'
// import { getLikelihood } from '../../data/likelihoodData'
// import { getPriorities } from '../../data/priorityData'
// import { getEnquirySources } from '../../data/enquirysourceData'

interface JobsKendoProps {
  lockEdit: boolean
}

function JobsKendo(props: JobsKendoProps) {
  const { showToast } = useToast()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const dateFormatter = new DateFormatterReverse()

  // Data states
  const [jobsData, setJobsData] = useState<jobsGrid[]>([])
  const [totalRecords, setTotalRecords] = useState(0)
  const [loading, setLoading] = useState(false)

  // Filter states for toggle buttons
  const [showCompleted, setShowCompleted] = useState(false)
  const [showDeleted, setShowDeleted] = useState(false) 
  const [showEngineerComments, setShowEngineerComments] = useState(false)
  const [showRemedialWorks, setShowRemedialWorks] = useState(false)

  // Form states
  const [addToggle, setAddToggle] = useState(false)
  const [editToggle, setEditToggle] = useState(false)
  const [editJobId, setEditJobId] = useState<number | null>(null)

  // Dropdown data states
  const [clientData, setClientData] = useState<clientsGrid[]>([])
  const [siteData, setSiteData] = useState<getSiteTypes[]>([])
  const [departmentData, setDepartmentData] = useState<dropDown[]>([])
  const [employeeData, setEmployeeData] = useState<dropDown[]>([])
  const [workTypeData, setWorkTypeData] = useState<dropDown[]>([])
  const [likelihoodData, setLikelihoodData] = useState<dropDown[]>([])
  const [priorityData, setPriorityData] = useState<dropDown[]>([])
  const [enquirySourceData, setEnquirySourceData] = useState<dropDown[]>([])

  // Server-side data state for Kendo Grid
  const [dataState, setDataState] = useState({
    skip: 0,
    take: 25,
    filter: undefined,
    sort: undefined
  })

  // Load dropdown data - simplified for now
  useEffect(() => {
    const loadDropdownData = async () => {
      try {
        // For now, we'll use empty arrays until we fix the imports
        // In a real implementation, you'd load all the dropdown data here
        setClientData([])
        setSiteData([])
        setDepartmentData([])
        setEmployeeData([])
        setWorkTypeData([])
        setLikelihoodData([])
        setPriorityData([])
        setEnquirySourceData([])
      } catch (error) {
        console.error('Error loading dropdown data:', error)
        showToast('Error loading form data', 'error')
      }
    }

    loadDropdownData()
  }, [showToast])

  // Load jobs data with server-side filtering and pagination
  const loadJobs = async (newDataState?: any) => {
    setLoading(true)
    const currentDataState = newDataState || dataState

    try {
      // Build server-side filter parameters for the API
      const apiParams = {
        // Pagination
        skip: currentDataState.skip || 0,
        take: currentDataState.take || 25,
        
        // Filter switches
        allocationType: 'all', // Placeholder, implement allocation type filter if needed
        showCompleted,
        showDeleted,
        showEngineerComments,
        showRemedialWorks,
        
        // Kendo Grid sorting
        sort: currentDataState.sort || [],
        
        // Kendo Grid filtering
        filter: currentDataState.filter || null
      }

      console.log('Loading jobs with API parameters:', apiParams)
      
      // Call the API with all parameters - you'll need to update getJobs to accept these
      const response = await getJobsWithPagination(apiParams)
      
      // The API should return { data: jobsGrid[], total: number }
      setJobsData(response.data)
      setTotalRecords(response.total)
      
    } catch (error) {
      console.error('Error loading jobs:', error)
      showToast('Error loading jobs', 'error')
    } finally {
      setLoading(false)
    }
  }

  // Load jobs when component mounts or filters change
  useEffect(() => {
    loadJobs()
  }, [showCompleted, showDeleted, showEngineerComments, showRemedialWorks])

  // Handle Kendo Grid data state changes (pagination, sorting, filtering)
  const handleDataStateChange = (event: any) => {
    const newDataState = event.dataState
    setDataState(newDataState)
    loadJobs(newDataState)
  }

  // Toggle filter handlers
  const handleShowCompleted = (event: any) => {
    setShowCompleted(event.target.value)
  }

  const handleShowDeleted = (event: any) => {
    setShowDeleted(event.target.value)
  }

  const handleShowEngineerComments = (event: any) => {
    setShowEngineerComments(event.target.value)
  }

  const handleShowRemedialWorks = (event: any) => {
    setShowRemedialWorks(event.target.value)
  }

  // Form handlers
  const handleAddToggle = () => {
    setAddToggle(true)
  }

  const handleEditToggle = (jobId: number) => {
    setEditJobId(jobId)
    setEditToggle(true)
  }

  const handleCloseAdd = () => {
    setAddToggle(false)
  }

  const handleCloseEdit = () => {
    setEditToggle(false)
    setEditJobId(null)
  }

  // Job operations
  const handleJobSubmit = async (jobData: any) => {
    try {
      await submitJob(jobData)
      showToast('Job created successfully', 'success')
      setAddToggle(false)
      loadJobs() // Reload data
    } catch (error) {
      console.error('Error creating job:', error)
      showToast('Error creating job', 'error')
    }
  }

  const handleJobUpdate = async (jobData: any) => {
    try {
      if (editJobId) {
        await updateJob(jobData, editJobId)
        showToast('Job updated successfully', 'success') 
        setEditToggle(false)
        setEditJobId(null)
        loadJobs() // Reload data
      }
    } catch (error) {
      console.error('Error updating job:', error)
      showToast('Error updating job', 'error')
    }
  }

  const handleJobComplete = async (jobId: number) => {
    try {
      await completeJob(jobId)
      showToast('Job completed successfully', 'success')
      loadJobs() // Reload data
    } catch (error) {
      console.error('Error completing job:', error)
      showToast('Error completing job', 'error')
    }
  }

  const handleJobDelete = async (jobId: number) => {
    try {
      await deleteJob(jobId)
      showToast('Job deleted successfully', 'success')
      loadJobs() // Reload data
    } catch (error) {
      console.error('Error deleting job:', error)
      showToast('Error deleting job', 'error')
    }
  }

  const handleExport = async () => {
    try {
      await exportJobs()
      showToast('Jobs exported successfully', 'success')
    } catch (error) {
      console.error('Error exporting jobs:', error)
      showToast('Error exporting jobs', 'error')
    }
  }

  return (
    <div style={{ padding: '1rem' }}>
      <h2>Jobs (Kendo Grid Implementation)</h2>
      <p style={{ color: '#666', marginBottom: '2rem' }}>
        This is the new Kendo Grid implementation with server-side filtering and pagination.
        Compare with the original at <strong>/jobs</strong>
      </p>

      <JobsKendoGrid
        data={{ data: jobsData, total: totalRecords }}
        loading={loading}
        onDataStateChange={handleDataStateChange}
        lockEdit={props.lockEdit}
        showCompleted={showCompleted}
        showDeleted={showDeleted}
        showEngineerComments={showEngineerComments}
        showRemedialWorks={showRemedialWorks}
        onShowCompleted={handleShowCompleted}
        onShowDeleted={handleShowDeleted}
        onShowEngineerComments={handleShowEngineerComments}
        onShowRemedialWorks={handleShowRemedialWorks}
        onAddToggle={handleAddToggle}
        onEditToggle={handleEditToggle}
        onJobComplete={handleJobComplete}
        onJobDelete={handleJobDelete}
        onExport={handleExport}
      />

      {/* Add Job Form */}
      <Drawer
        expanded={addToggle}
        onOverlayClick={handleCloseAdd}
        position="end"
        mode="overlay"
        width={600}
      >
        <DrawerContent>
          <div style={{ padding: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3>Add New Job</h3>
              <FaTimesCircle 
                style={{ cursor: 'pointer', fontSize: '1.5rem', color: '#666' }}
                onClick={handleCloseAdd}
              />
            </div>
            
            <AddJobForm
              clientsData={clientData}
              sitesData={siteData}
              departmentData={departmentData}
              employeesData={employeeData}
              workTypeData={workTypeData}
              likelihoodData={likelihoodData}
              priorityData={priorityData}
              enquirySourceData={enquirySourceData}
              onSubmit={handleJobSubmit}
              onCancel={handleCloseAdd}
            />
          </div>
        </DrawerContent>
      </Drawer>

      {/* Edit Job Form */}
      <Drawer
        expanded={editToggle}
        onOverlayClick={handleCloseEdit}
        position="end"
        mode="overlay"
        width={800}
      >
        <DrawerContent>
          <div style={{ padding: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3>Edit Job</h3>
              <FaTimesCircle 
                style={{ cursor: 'pointer', fontSize: '1.5rem', color: '#666' }}
                onClick={handleCloseEdit}
              />
            </div>
            
            {editJobId && (
              <EditJobForm
                key={editJobId}
                onJobId={editJobId}
                clientsData={clientData}
                sitesData={siteData}
                departmentData={departmentData}
                employeesData={employeeData}
                workTypeData={workTypeData}
                likelihoodData={likelihoodData}
                priorityData={priorityData}
                enquirySourceData={enquirySourceData}
                onSubmit={handleJobUpdate}
                onCancel={handleCloseEdit}
                lockEdit={props.lockEdit}
              />
            )}
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  )
}

export default JobsKendo